import React, { useState, useRef } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@cargomatic/ui';
import { Download } from 'lucide-react';
import Select from 'components/v2/select';
import CargomaticInput from 'components/v2/input';
import { useTranslation } from 'react-i18next';
import { FileUploader } from 'components/v2/FileUploader';
import validator from 'email-validator';
import { showToast } from 'stores/ToastStore';
import { CSV_FILE_TYPES } from 'config/accepted-file-types';
import { bulkRegisterUsers } from 'utils/UserWebAPIUtils';
import XLSX from 'xlsx';
import fileDownload from 'js-file-download';
import SessionStore from 'stores/SessionStore';

const items = [
  {
    userType: 'Admin',
    localizationSlug: 'ADMIN',
    fields: [
      '_type',
      'username',
      'email',
      'first_name',
      'last_name',
      'password',
    ],
  },
  {
    userType: 'Driver',
    localizationSlug: 'DRIVER',
    titleize: true,
    fields: [
      '_type',
      'carrier',
      'email',
      'first_name',
      'last_name',
      'phone',
      'password',
      'username',
    ],
  },
  {
    userType: 'Carrier',
    localizationSlug: 'CARRIER',
    titleize: true,
    fields: [
      '_type',
      'company_name',
      'email',
      'dot_number',
      'first_name',
      'last_name',
      'password',
      'phone',
      'phone_extension',
      'subsidiary',
    ],
  },
  {
    userType: 'Shipper',
    localizationSlug: 'SHIPPER',
    titleize: true,
    fields: [
      '_type',
      'company_name',
      'email',
      'sales_rep',
      'first_name',
      'last_name',
      'password',
      'phone',
      'master_account',
      'subsidiary',
    ],
  },
];
const BulkRegisterUsers = () => {
  const { t } = useTranslation();

  const currentUser = SessionStore.getUser();

  const [templateName, setTemplateName] = useState('');
  const [templateFields, setTemplateFields] = useState([]);
  const [emails, setEmails] = useState(currentUser?.email ?? '');
  const fileUploaderRef = useRef(null);
  const [file, setFile] = useState(null);

  const onSelectTemplate = ({ userType, fields }) => {
    setTemplateName(userType);
    setTemplateFields(fields);
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([templateFields]);
    const csv = XLSX.utils.sheet_to_csv(ws);
    fileDownload(csv, `${templateName}.csv`);
  };

  const parseEmails = (value = '') =>
    value
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter(Boolean);

  const isEmailInvalid = () => {
    const emailIds = parseEmails(emails);
    if (!emailIds.length) {
      return true;
    }
    return emailIds.some((ea) => !validator.validate(ea));
  };

  const handleSubmit = async () => {
    if (!emails) {
      showToast('', t('EMAIL_REQUIRED'), 'error');
      return;
    }
    if (isEmailInvalid()) {
      showToast('', t('INVALID_EMAILS'), 'error');
      return;
    }

    if (!file) {
      showToast('', t('CSV_REQUIRED'), 'error');
      return;
    }
    const emailIds = parseEmails(emails);
    try {
      await bulkRegisterUsers(file, emailIds);
    } catch (e) {
      console.error(e?.message);
    }
  };

  const handleFileUpload = (files = []) => {
    if (files?.length) {
      setFile(files[0]);
    } else {
      setFile(null);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('BULK_REGISTER_USERS')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('DRAG_AND_DROP_FOR_BULK_REGISTER_USERS')}
        </p>
        <Select
          selected={templateName}
          onSelect={onSelectTemplate}
          caption={t('SELECT_USER')}
          textKey="localizationSlug"
          valueKey="userType"
          items={items}
        />
        {templateName && (
          <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
            <Download className="h-4 w-4 mr-2" />
            {t('TEMPLATE')}
          </Button>
        )}
        <div>
          <label
            htmlFor="bulk-report-email"
            className="text-sm font-medium mb-2 block"
          >
            {t('COMMA_SEPARATED_EMAILS_FOR_RESULTS')}
          </label>
          <CargomaticInput
            id="bulk-report-email"
            placeholder={t('EMAIL', { count: 2 })}
            value={emails}
            onChange={(value) => setEmails(value)}
          />
        </div>
        <FileUploader
          fileTypes={CSV_FILE_TYPES}
          fileTypesLabel={t('CSV')}
          ref={fileUploaderRef}
          onChange={handleFileUpload}
        />
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={handleSubmit}>{t('SUBMIT')}</Button>
      </CardFooter>
    </Card>
  );
};

export default BulkRegisterUsers;
